import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { aneelConsistencyParams, fetchAneelConsistency } from '@/services/aneel';
import type {
  AneelDivergentPair,
  AneelFilterOption,
  AneelPairSide,
} from '@/services/types';

const PAGE_SIZE = 25;

/** A full CNPJ, the only key `/aneel/e/:cnpj` accepts. */
const CNPJ_DIGITS = 14;

/** The URL spelling of the cross-agency filter, seeded and read in one place. */
function parseCross(value: string | null): boolean | null {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
}

/**
 * Decided ANEEL cases of the same nature and band that ended differently.
 *
 * This screen deliberately does NOT look like ANATEL's consistency queue, and
 * the difference is the point. There, a model read the reasoning of both
 * decisions and returned a verdict that a QA pass then checked against the
 * source; every row carries a model name, a prompt version and an evidence span.
 *
 * ANEEL publishes no reasoning text at all, so nothing here has been judged by
 * anything. What is left is arithmetic: two penalties moved differently. A
 * reader arriving from the ANATEL screens will assume adjudication unless the
 * page says otherwise, so the refusal that says otherwise is rendered ABOVE the
 * queue rather than beneath it.
 */
export function AneelConsistencyPage() {
  const { t, i18n } = useTranslation(['aneel', 'common']);
  // Seeded from the URL so the case page's "comparables in this nature" link
  // lands on that nature's queue rather than on page one of everything.
  const [seed] = useSearchParams();
  const [nature, setNature] = useState(seed.get('nature') ?? '');
  const [band, setBand] = useState(seed.get('band') ?? '');
  const [agency, setAgency] = useState(seed.get('agency') ?? '');
  const [cross, setCross] = useState<boolean | null>(parseCross(seed.get('cross_agency')));
  const [combination, setCombination] = useState(seed.get('combination') ?? '');

  const filters = {
    nature: nature || null,
    band: band || null,
    agency: agency || null,
    cross_agency: cross,
    combination: combination || null,
  };
  const filtered = Boolean(nature || band || agency || cross !== null || combination);

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['aneel', 'consistency', filters],
    queryFn: ({ pageParam }) =>
      fetchAneelConsistency({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    // Driven by the server's own total, never by "the last page came back
    // full": a result set that is an exact multiple of PAGE_SIZE would
    // otherwise leave a button promising rows that do not exist.
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });

  // The funnel, the verdict and the options are corpus-level and identical on
  // every page; the pairings follow the filter and are identical across pages
  // of one filter. Only the ROWS accumulate.
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];

  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('aneel:consistency.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('aneel:consistency.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="aneel:consistency.explainer" />

      <div className="flex justify-end">
        <ExportButton
          path={`/api/v1/aneel/export/consistency?${aneelConsistencyParams(filters).toString()}`}
        />
      </div>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      {error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : isPending || !data ? (
        <SkeletonLoader rows={8} />
      ) : (
        <div className="space-y-4">
          {/* Above the queue, not below it. A reader who has used the ANATEL
              screens will read "consistency" as "adjudicated" unless told
              otherwise, and by the time they reach a footnote they have already
              read the rows as findings. */}
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground"
          >
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{t('aneel:consistency.notAFinding')}</span>
          </p>

          <section className="rounded-lg border bg-card p-4">
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('aneel:consistency.funnel.title')}
            </h2>
            <Answer answer={data.reach} help={t('aneel:consistency.funnel.help')}>
              {(funnel) => (
                <div className="space-y-3">
                  {/* The whole funnel, not just its last number. `divergent`
                      alone reads as "ANEEL decided 354 comparable cases
                      inconsistently"; the three counts above it are what make
                      that sentence untrue. */}
                  <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {(
                      [
                        ['pairs', funnel.total_pairs],
                        ['bothDecided', funnel.both_sides_decided],
                        ['divergent', funnel.divergent],
                        ['cases', funnel.cases_covered],
                      ] as const
                    ).map(([key, value]) => (
                      <div key={key} className="rounded-md border bg-background p-3">
                        <dt className="text-xs text-muted-foreground">
                          {t(`aneel:consistency.funnel.${key}`)}
                        </dt>
                        <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
                          {number.format(value)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                    {t('aneel:consistency.funnel.coverage', {
                      cases: number.format(funnel.cases_covered),
                      decided: number.format(funnel.decided_cases),
                      share: percent.format(funnel.case_coverage),
                      cross: number.format(funnel.cross_agency),
                      crossShare: percent.format(funnel.cross_agency_share),
                      single: number.format(funnel.single_band),
                    })}
                  </p>
                </div>
              )}
            </Answer>
            <div className="mt-3">
              <FieldGuide i18nKey="aneel:consistency.guide.funnel" />
            </div>
          </section>

          {/* The refusal is the deliverable of this panel, so it gets a panel
              rather than a footnote. An absent panel is indistinguishable from
              one nobody built. */}
          <section className="rounded-lg border bg-card p-4">
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('aneel:consistency.verdict.title')}
            </h2>
            <Answer answer={data.verdict_layer} help={t('aneel:consistency.verdict.help')}>
              {/* Rendered for the day it answers: today it refuses, and a panel
                  that rendered nothing on an answer would hide that change. */}
              {(coverage) => (
                <p className="text-sm text-foreground" data-verdict-adjudicated={coverage.adjudicated}>
                  {t('aneel:consistency.verdict.answered', {
                    adjudicated: number.format(coverage.adjudicated),
                    divergent: number.format(coverage.divergent),
                    share: percent.format(coverage.share),
                  })}
                </p>
              )}
            </Answer>
          </section>

          {/* Options come from the divergent population itself, with the pair
              count beside each, so no filter can match nothing. */}
          <section className="rounded-lg border bg-card p-3" data-pair-filters>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('aneel:consistency.filters.title')}
            </h2>
            <div className="flex flex-wrap items-end gap-3">
              <Select
                id="aneel-pair-nature"
                label={t('aneel:consistency.filters.nature')}
                any={t('aneel:consistency.filters.any')}
                value={nature}
                onChange={setNature}
                options={data.options.natures}
              />
              <Select
                id="aneel-pair-band"
                label={t('aneel:consistency.filters.band')}
                any={t('aneel:consistency.filters.any')}
                value={band}
                onChange={setBand}
                options={data.options.bands}
              />
              <Select
                id="aneel-pair-agency"
                label={t('aneel:consistency.filters.agency')}
                any={t('aneel:consistency.filters.any')}
                value={agency}
                onChange={setAgency}
                options={data.options.agencies}
              />
              <div className="space-y-1">
                <label
                  htmlFor="aneel-pair-cross"
                  className="block text-xs font-medium text-foreground"
                >
                  {t('aneel:consistency.filters.crossAgency')}
                </label>
                <select
                  id="aneel-pair-cross"
                  value={cross === null ? '' : String(cross)}
                  onChange={(event) => setCross(parseCross(event.target.value))}
                  className="max-w-[13rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
                >
                  <option value="">{t('aneel:consistency.filters.crossAny')}</option>
                  <option value="true">{t('aneel:consistency.filters.crossOnly')}</option>
                  <option value="false">{t('aneel:consistency.filters.sameOnly')}</option>
                </select>
              </div>
              <Select
                id="aneel-pair-combination"
                label={t('aneel:consistency.filters.combination')}
                any={t('aneel:consistency.filters.any')}
                value={combination}
                onChange={setCombination}
                options={data.options.combinations}
                render={(option) => combinationLabel(t, option.value)}
              />
              {filtered ? (
                <button
                  type="button"
                  onClick={() => {
                    setNature('');
                    setBand('');
                    setAgency('');
                    setCross(null);
                    setCombination('');
                  }}
                  className="rounded-md border px-3 py-1.5 text-sm text-foreground"
                  data-clear-filters
                >
                  {t('aneel:consistency.filters.clear')}
                </button>
              ) : null}
            </div>
            <div className="mt-3">
              <FieldGuide i18nKey="aneel:consistency.guide.filters" />
            </div>
          </section>

          {data.pairings.length > 0 ? (
            <section className="rounded-lg border bg-card p-4" data-pairings>
              <h2 className="mb-1 text-sm font-semibold text-foreground">
                {t('aneel:consistency.pairings.title')}
              </h2>
              <p className="mb-3 text-xs text-muted-foreground">
                {t('aneel:consistency.pairings.help')}
              </p>
              <ul className="space-y-1">
                {data.pairings.map((pairing) => (
                  <li
                    key={pairing.label}
                    className="flex items-baseline justify-between gap-3 border-b pb-1 text-sm last:border-b-0"
                    data-pairing={pairing.label}
                    data-pairs={pairing.pairs}
                  >
                    {/* Each combination narrows the queue to itself: the row's
                        count and the filtered total are one predicate. */}
                    <button
                      type="button"
                      onClick={() => setCombination(pairing.label)}
                      aria-label={t('aneel:consistency.pairings.narrow', {
                        n: number.format(pairing.pairs),
                        label: combinationLabel(t, pairing.label),
                      })}
                      className="text-left text-primary underline-offset-2 hover:underline"
                    >
                      {combinationLabel(t, pairing.label)}
                    </button>
                    <span className="tabular-nums text-muted-foreground">
                      {number.format(pairing.pairs)} · {percent.format(pairing.share)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* Its own element, with the raw total and a staleness flag on it.
              `keepPreviousData` keeps the PREVIOUS query's total on screen for
              the whole of the next fetch, and the rendered sentence gives no
              way to tell which query it describes. */}
          <p
            className="text-sm text-foreground"
            aria-live="polite"
            data-testid="aneel-consistency-total"
            data-loaded={items.length}
            data-total={data.total}
            data-stale={String(isPlaceholderData)}
          >
            {t('common:pagination.loaded', {
              loaded: number.format(items.length),
              total: number.format(data.total),
            })}
          </p>

          {items.length === 0 ? (
            <EmptyState hint={t('aneel:consistency.emptyHint')} />
          ) : (
            <>
              {/* Bounded height with its own scrollbar, so batches grow inside
                  the box rather than pushing the filters several screens up.
                  The sentinel sits INSIDE it, because that is what scrolls. */}
              <div
                ref={scrollRef}
                className="max-h-[70vh] overflow-auto rounded-lg border p-3"
                role="region"
                aria-label={t('aneel:consistency.listLabel')}
              >
                <ol className="space-y-3">
                  {items.map((pair) => (
                    <PairCard key={pair.pair_key} pair={pair} currency={currency} />
                  ))}
                </ol>

                <div ref={sentinelRef} aria-hidden className="h-px" />

                <div className="flex justify-center py-3">
                  {hasNextPage ? (
                    <button
                      type="button"
                      onClick={loadMore}
                      disabled={isFetchingNextPage}
                      className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
                    >
                      {isFetchingNextPage
                        ? t('common:pagination.loadingMore')
                        : t('common:pagination.loadMore', { count: PAGE_SIZE })}
                    </button>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {t('common:pagination.allLoaded', { total: number.format(data.total) })}
                    </p>
                  )}
                </div>
              </div>

              <FieldGuide i18nKey="aneel:consistency.guide.pair" />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** `MAINTAINED/REDUCED` as the reader's two trajectory words. */
function combinationLabel(t: (key: string, fallback: string) => string, label: string): string {
  return label
    .split('/')
    .map((code) => t(`aneel:overview.trajectories.${code}`, code))
    .join(' · ');
}

function Select({
  id,
  label,
  any,
  value,
  onChange,
  options,
  render,
}: {
  id: string;
  label: string;
  any: string;
  value: string;
  onChange: (value: string) => void;
  options: AneelFilterOption[];
  render?: (option: AneelFilterOption) => string;
}) {
  const { i18n } = useTranslation();
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-foreground">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="max-w-[16rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
      >
        <option value="">{any}</option>
        {/* `cases` counts PAIRS on this screen: the option's own reach. */}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {render ? render(option) : option.value} ({number.format(option.cases)})
          </option>
        ))}
      </select>
    </div>
  );
}

function PairCard({
  pair,
  currency,
}: {
  pair: AneelDivergentPair;
  currency: Intl.NumberFormat;
}) {
  const { t } = useTranslation(['aneel']);

  return (
    <li className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-baseline gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{pair.nature ?? '—'}</span>
        {pair.shared_bands.length > 0 ? (
          <span>{t('aneel:consistency.shared', { bands: pair.shared_bands.join('; ') })}</span>
        ) : null}
        {/* On the row, not in a caption: 205 of 354 pairs cross inspecting
            bodies, and that confound explains a difference in outcome at least
            as readily as inconsistency does. */}
        {pair.cross_agency ? (
          <span className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[0.65rem] text-foreground">
            {t('aneel:consistency.crossAgency')}
          </span>
        ) : null}
      </div>

      {/* Side by side and visually equal. Labelled A and B rather than
          "case" and "comparable", because the pair is stored unordered and
          neither side is the baseline the other deviates from. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <SideCard side={pair.side_a} label={t('aneel:consistency.sideA')} currency={currency} />
        <SideCard side={pair.side_b} label={t('aneel:consistency.sideB')} currency={currency} />
      </div>
    </li>
  );
}

function SideCard({
  side,
  label,
  currency,
}: {
  side: AneelPairSide;
  label: string;
  currency: Intl.NumberFormat;
}) {
  const { t, i18n } = useTranslation(['aneel']);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 0,
  });

  return (
    <div className="space-y-1 rounded-md border bg-background p-3">
      <p className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">{label}</p>
      <Link
        to={`/aneel/c/${encodeURIComponent(side.case_key)}`}
        className="block font-medium tabular-nums text-primary underline-offset-4 hover:underline"
      >
        {side.notice_number}
      </Link>
      {/* The company opens its profile only on a full CNPJ, the one key the
          profile route accepts; a truncated one stays plain text. */}
      <p className="text-xs text-muted-foreground">
        {side.entity_cnpj && side.entity_cnpj.length === CNPJ_DIGITS && side.entity_name ? (
          <Link
            to={`/aneel/e/${side.entity_cnpj}`}
            className="text-primary underline-offset-2 hover:underline"
            aria-label={t('aneel:consistency.sideEntity', { name: side.entity_name })}
          >
            {side.entity_name}
          </Link>
        ) : (
          (side.entity_name ?? '—')
        )}
      </p>
      <p className="text-sm text-foreground">
        {side.trajectory
          ? t(`aneel:overview.trajectories.${side.trajectory}`, side.trajectory)
          : t('aneel:overview.trajectories.UNCLASSIFIED')}
      </p>
      <p className="text-xs tabular-nums text-muted-foreground">
        {side.original_amount === null ? '—' : currency.format(side.original_amount)}
        {side.final_amount === null ? '' : ` → ${currency.format(side.final_amount)}`}
        {/* The retained share is what makes two amounts comparable across cases
            charged very differently. Null where the charge was zero, which is a
            real value in this corpus rather than a missing one. */}
        {side.retained === null ? '' : ` · ${percent.format(side.retained)}`}
      </p>
      <p className="text-xs text-muted-foreground">
        {[side.decided_on, side.inspecting_agency].filter(Boolean).join(' · ')}
      </p>
    </div>
  );
}
